import { generatePath } from 'react-router-dom';
import URL from '../../../../constants/api-constants';
import http from '../../../../store/http/http-common';
import { ChangeControllerSettingsAction } from '../../../../enums/action';
import { toast } from 'react-toastify';
import { ChangeControllerSettingsActionType } from '../Settings/settingsActions';
import i18n from '../../../../infrastructure/i18n';
import { number, object } from 'yup';
import ValidationAction from '../../../../types/validation-action';

type SubmitActionFunction = (id: string, value: string) => Promise<void>;

export const changeControllerPowerShare = async (
  controller_id: string,
  action: ChangeControllerSettingsAction,
  newValue?: string,
) => {
  try {
    const body: { action: ChangeControllerSettingsAction; value?: string } = { action };

    if (newValue) {
      body.value = newValue;
    }

    await http.put(
      generatePath(URL.CONTROLLER_SETTINGS, {
        id: controller_id,
      }),
      body,
    );
    toast.success('Action successfully executed');
  } catch (error) {
    toast.error(error.message);
  }
};

const submitPowerShareEnableAction: SubmitActionFunction = async (id: string, value: string) => {
  const data = JSON.stringify({ enablePowerShare: !!Number(value) });
  await changeControllerPowerShare(id, ChangeControllerSettingsAction.POWER_SHARING_ENABLE, data);
};

const submitAllowFullPowerAction: SubmitActionFunction = async (id: string, value: string) => {
  const data = JSON.stringify({ allowFullPower: !!Number(value) });
  await changeControllerPowerShare(id, ChangeControllerSettingsAction.FULL_POWER_ALLOWING, data);
};

const submitRunPreHeatWithFaultAction: SubmitActionFunction = async (id: string, value: string) => {
  const data = JSON.stringify({ runWithFault: !!Number(value) });
  await changeControllerPowerShare(id, ChangeControllerSettingsAction.PRE_HEAT_RUN_WITH_FAULT, data);
};

const submitPowerShareAmpsAction: SubmitActionFunction = async (id: string, value: string) => {
  const data = JSON.stringify({ shareAmps: Number(value) });
  await changeControllerPowerShare(id, ChangeControllerSettingsAction.POWER_SHARING_AMPERAGE, data);
};

export const getControllerPowerShareStatus = (enabled: boolean) => {
  if (!enabled) {
    return i18n.t('statuses.output.disabled');
  }

  return i18n.t('statuses.output.enabled');
};

export type ChangeControllerPowerShareButtonActionType = Omit<
  ChangeControllerSettingsActionType,
  'inputType' | 'actionField'
>;

export const POWER_SHARE_CURRENT_LIMIT =  { MIN: 0, MAX: 1000 };

export const powerShareAmpsAction: ChangeControllerSettingsActionType &
  ValidationAction & { onSubmit: SubmitActionFunction } = {
    actionName: ChangeControllerSettingsAction.POWER_SHARING_ENABLE,
    labelText: i18n.t('controllers.settings.power_share_current_limit'),
    actionField: 'power_share_current_limit',
    inputType: 'number',
    onSubmit: submitPowerShareAmpsAction,
    validationSchema: object({
      power_share_current_limit: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .min(
          POWER_SHARE_CURRENT_LIMIT.MIN,
          i18n.t('errors.numberValue.min', '', { minNum: POWER_SHARE_CURRENT_LIMIT.MIN }),
        )
        .max(
          POWER_SHARE_CURRENT_LIMIT.MAX,
          i18n.t('errors.numberValue.max', '', { maxNum: POWER_SHARE_CURRENT_LIMIT.MAX }),
        )
    }),
  };

export const powerShareBoolActions: (Omit<ChangeControllerSettingsActionType, 'inputType' | 'actionField'> & ValidationAction & {
  actionField: 'is_power_share_enabled' | 'is_allowed_to_run_at_100' | 'preheat_run_if_faulted';
  btnText: string;
  onSubmit: SubmitActionFunction;
})[] = [
  {
    actionName: ChangeControllerSettingsAction.POWER_SHARING_ENABLE,
    labelText: 'Power Sharing',
    actionField: 'is_power_share_enabled',
    btnText: 'Update',
    onSubmit: submitPowerShareEnableAction,
  },
  {
    actionName: ChangeControllerSettingsAction.FULL_POWER_ALLOWING,
    labelText: 'Full Power Allowed',
    actionField: 'is_allowed_to_run_at_100',
    btnText: 'Update',
    onSubmit: submitAllowFullPowerAction,
  },
  {
    actionName: ChangeControllerSettingsAction.PRE_HEAT_RUN_WITH_FAULT,
    labelText: 'Pre-Heat Run If Faulted',
    actionField: 'preheat_run_if_faulted',
    btnText: 'Update',
    onSubmit: submitRunPreHeatWithFaultAction,
  },
];

export const consoleButtonActions: ChangeControllerPowerShareButtonActionType[] = [
  {
    actionName: ChangeControllerSettingsAction.CHANNEL_CONSOLE_4_6,
    labelText: '4x6 Channel Consoles',
  },
  {
    actionName: ChangeControllerSettingsAction.CHANNEL_CONSOLE_2_12,
    labelText: '2x12 Channel Consoles',
  },
  {
    actionName: ChangeControllerSettingsAction.CHANNEL_CONSOLE_1_18,
    labelText: '1x18 Channel Consoles',
  },
  {
    actionName: ChangeControllerSettingsAction.CHANNEL_CONSOLE_1_12_GEN,
    labelText: '1x12 Channel Generator Panel',
  },
];
